import { LayersList } from '@deck.gl/core';
import { GoogleMapsOverlay } from '@deck.gl/google-maps';
import { useEffect, useRef, useState } from 'react';

import { canCreateWebGL2Context } from './webglAvailability';

interface UseDeckOverlayParams {
  layers: LayersList | undefined;
  map: google.maps.Map | null;
}

export default function useDeckOverlay({ layers, map }: UseDeckOverlayParams) {
  const overlayRef = useRef<GoogleMapsOverlay | null>(null);
  const [isOverlayAttached, setIsOverlayAttached] = useState(false);
  const [isMapViewportReady, setIsMapViewportReady] = useState(false);
  const [isWebGLAvailable, setIsWebGLAvailable] = useState(true);

  useEffect(() => {
    setIsOverlayAttached(false);
    setIsMapViewportReady(false);

    if (!map) return;

    // deck.gl creates its WebGL device asynchronously and never awaits the
    // failure, so a browser refusing a context surfaces as an unhandled promise
    // rejection (Sentry HORIZON-FE-2S0). Probe synchronously and skip the overlay
    // when no context can be created.
    const webGLAvailable = canCreateWebGL2Context();
    setIsWebGLAvailable(webGLAvailable);

    // deck.gl reads the viewport as it draws, so layers set before the first
    // 'idle' hit a null pixelUnprojectionMatrix and throw.
    // Captured at registration so teardown can still remove the listener after
    // the Maps SDK global is gone.
    const mapsEvent = google.maps.event;
    const idleListener = map.addListener('idle', () => {
      mapsEvent.removeListener(idleListener);
      setIsMapViewportReady(true);
    });

    let renderingTypeListener: google.maps.MapsEventListener | null = null;

    if (webGLAvailable) {
      const attachOverlay = () => {
        // pointer-events: none keeps the overlay container from blocking native
        // Google Maps controls; deck.gl picking is driven by map listeners.
        const overlay = new GoogleMapsOverlay({ style: { pointerEvents: 'none' } });
        overlay.setMap(map);
        overlayRef.current = overlay;
        setIsOverlayAttached(true);
      };

      // Attaching while Google still reports the map as UNINITIALIZED crashes the
      // Live Map (Sentry HORIZON-FE-2XP): deck.gl's setMap() then registers its
      // own renderingtype_changed listener, and finalizing before the type
      // resolves nulls its map reference without removing that listener, which
      // later builds a deck against a null map. Defer until the type resolves.
      const mapRenderingTypeIsUninitialized =
        map.getRenderingType() === window.google.maps.RenderingType.UNINITIALIZED;

      if (mapRenderingTypeIsUninitialized) {
        renderingTypeListener = map.addListener('renderingtype_changed', () => {
          renderingTypeListener?.remove();
          renderingTypeListener = null;
          attachOverlay();
        });
      } else {
        attachOverlay();
      }
    }

    return () => {
      // A leftover idle listener would mark a map this component no longer owns
      // as viewport-ready, and a still-pending rendering-type listener would
      // attach an overlay to it.
      mapsEvent.removeListener(idleListener);
      renderingTypeListener?.remove();
      // Detach before Google Maps tears down the map DOM: a stray draw cycle
      // against the removed div throws (Sentry HORIZON-FE-2WK). finalize() also
      // frees the deck.gl WebGL instance.
      overlayRef.current?.finalize();
      overlayRef.current = null;
    };
  }, [map]);

  useEffect(() => {
    if (!isOverlayAttached || !isMapViewportReady) return;

    overlayRef.current?.setProps({ layers });
  }, [isMapViewportReady, isOverlayAttached, layers]);

  return { isWebGLAvailable };
}
